import { Button } from '@/components/ui/button.tsx';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemTitle,
} from '@/components/ui/item.tsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ProductAnalysisRun, StartProductAnalysisResponse } from '@ozon-ai/analysis/contracts';
import type { ProductCatalogItem } from '@ozon-ai/ozon/contracts';
import { cn } from 'cn';
import { api } from '../../api/client';
import { PeriodPicker } from '../../components/period-picker/period-picker';
import { usePeriodPicker } from '../../components/period-picker/use-period-picker';
import { usePeriodPresets } from '../../components/period-picker/use-period-presets';
import { useTelegram } from '../../context/TelegramContext';
import {
  formatAnalysisDate,
  runStatusLabel,
} from '../../components/product-analysis/productAnalysisLabels';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ContentCard } from '../../ui/ContentCard';
import { ErrorText, HintText, PageShell } from '../../ui/PageShell';
import { StatusBadge } from '../../ui/StatusBadge';

export function SkuAnalysisDetailPage() {
  const { offerId: offerIdParam } = useParams();
  const offerId = offerIdParam ? decodeURIComponent(offerIdParam) : '';
  const navigate = useNavigate();
  const { webApp } = useTelegram();

  const { presets, primaryPresetId, loading: presetsLoading } = usePeriodPresets();
  const period = usePeriodPicker({
    presets,
    primaryPresetId,
    presetsReady: !presetsLoading,
  });
  const [catalogItem, setCatalogItem] = useState<ProductCatalogItem | null>(null);
  const [history, setHistory] = useState<ProductAnalysisRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayName = useMemo(
    () => catalogItem?.name ?? history[0]?.productName ?? offerId,
    [catalogItem, history, offerId]
  );

  const load = useCallback(async () => {
    if (!offerId) return;
    setLoading(true);
    setError(null);
    try {
      const [catalog, runs] = await Promise.all([
        api.get<ProductCatalogItem[]>('/products/catalog'),
        api.get<ProductAnalysisRun[]>(
          `/product-analyses?offerId=${encodeURIComponent(offerId)}&limit=20`
        ),
      ]);
      setCatalogItem(catalog.find((item) => item.offerId === offerId) ?? null);
      setHistory(runs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить данные SKU');
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRun() {
    if (!offerId) return;
    if (!period.selection) {
      setError(period.localError ?? 'Укажите период');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const started = await api.post<StartProductAnalysisResponse>('/product-analyses/run', {
        offerId,
        period: period.selection,
      });
      webApp?.HapticFeedback?.notificationOccurred('success');
      navigate(`/analyses/sku/${encodeURIComponent(offerId)}/runs/${encodeURIComponent(started.runId)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось запустить анализ');
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setBusy(false);
    }
  }

  if (!offerId) {
    return (
      <PageShell title="Анализ SKU">
        <ErrorText>Не указан артикул</ErrorText>
      </PageShell>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <PageShell
      title={displayName}
      action={
        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/analyses/sku')}>
          Назад
        </Button>
      }
    >
      <ContentCard>
        <div className="flex items-start gap-3">
          {catalogItem?.imageUrl ? (
            <img
              src={catalogItem.imageUrl}
              width={72}
              height={72}
              className="size-[72px] shrink-0 rounded-lg object-cover"
              alt=""
            />
          ) : null}
          <div className="min-w-0 flex-1 flex flex-col gap-1">
            <p className="text-base font-semibold">{displayName}</p>
            <p className="text-xs text-muted-foreground">{offerId}</p>
            {(catalogItem?.ozonSku ?? history[0]?.ozonSku) && (
              <p className="text-xs text-muted-foreground">
                SKU Ozon: {catalogItem?.ozonSku ?? history[0]?.ozonSku}
              </p>
            )}
          </div>
        </div>
      </ContentCard>

      <ContentCard>
        <p className="mb-3 text-sm font-semibold">Запустить анализ</p>
        <div className="flex flex-col gap-3">
          <PeriodPicker {...period} dateFieldIdPrefix="sku-analysis" />
          {period.localError ? <ErrorText>{period.localError}</ErrorText> : null}
          <Button type="button" disabled={busy || !period.selection} onClick={() => void handleRun()}>
            {busy ? 'Запуск…' : 'Запустить анализ'}
          </Button>
        </div>
      </ContentCard>

      {error && <ErrorText>{error}</ErrorText>}

      <ContentCard>
        <p className="mb-2 text-sm font-semibold">История анализов</p>
        {history.length === 0 ? (
          <HintText>Анализов пока нет. Запустите первый анализ за выбранный период.</HintText>
        ) : (
          <div className="flex flex-col">
            {history.map((run, index) => (
              <Item
                key={run.id}
                size="sm"
                className={cn(
                  'cursor-pointer rounded-none border-0 border-b border-border/60 px-0 hover:bg-muted/50',
                  index === history.length - 1 && 'border-b-0',
                )}
                onClick={() =>
                  navigate(
                    `/analyses/sku/${encodeURIComponent(offerId)}/runs/${encodeURIComponent(run.id)}`
                  )
                }
              >
                <ItemContent>
                  <ItemTitle className="text-sm font-normal">
                    {formatAnalysisDate(run.finishedAt ?? run.createdAt)} · {run.periodFrom}–{run.periodTo}
                  </ItemTitle>
                </ItemContent>
                <ItemActions>
                  <StatusBadge status={run.status}>{runStatusLabel(run.status)}</StatusBadge>
                </ItemActions>
              </Item>
            ))}
          </div>
        )}
      </ContentCard>
    </PageShell>
  );
}
